"use client";
import { useEffect, useRef, useState } from "react";
import { type Step } from "./surveySteps";
import { Option } from "./surveySteps";
import { Check, ChevronDown, ChevronLeft, ChevronRight, Phone, Search } from "lucide-react";
import {
  getSurveySteps,
  postSurveyFill,
} from "../services/survey/surveyService";
import { useNavigate } from "react-router-dom";
import { dictionary } from "../utils/dictionary";
import { Language } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";
import { WORLD_COUNTRIES } from "../const/worldCountryCodes";
import { decodeTokenService } from "../services/decodeTokenService";

const DEFAULT_SURVEY_COUNTRY = WORLD_COUNTRIES.find((c) => c.code === "CO")!;

interface InitialSurveyProps {
  missingStepIds?: string[];
}

export default function InitialSurvey({ missingStepIds = [] }: InitialSurveyProps) {
  const [currentStepId, setCurrentStepId] = useState<string>("");
  const [history, setHistory] = useState<string[]>([]);
  const [surveySteps, setSurveySteps] = useState<Step[]>([]);
  const [currentStep, setCurrentStep] = useState<Step | undefined>(undefined);
  const [answeredIds, setAnsweredIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const { language, country } = useAuth();

  const isResuming = missingStepIds.length > 0;
  const userId = decodeTokenService.getUserIdFromToken() || localStorage.getItem("user_id") || "";

  // Load all steps
  useEffect(() => {
    getSurveySteps("default").then((steps) => {
      const allSteps = steps.data as Step[];
      setSurveySteps(allSteps);

      // Build the set of already-answered step IDs
      if (isResuming) {
        const missingSet = new Set(missingStepIds);
        const alreadyAnswered = new Set(
          allSteps
            .filter((s) => !missingSet.has(s.id) && s.type !== "summary")
            .map((s) => s.id)
        );
        setAnsweredIds(alreadyAnswered);
      }

      // Determine first step to show
      let firstStep: Step | undefined;
      if (isResuming) {
        firstStep = allSteps.find((s) => s.id === missingStepIds[0]);
      }
      if (!firstStep) {
        firstStep =
          allSteps.find((s: Step) => s.is_first_step) ||
          allSteps.find((s: Step) => s.id === "fullName") ||
          allSteps.find((s: Step) => s.id === "mobelNumber") ||
          allSteps[0];
      }
      if (firstStep) {
        setCurrentStepId(firstStep.id);
      }
    });
  }, []);

  // Sync currentStep when currentStepId changes
  useEffect(() => {
    if (surveySteps.length > 0 && currentStepId) {
      setCurrentStep(surveySteps.find((s) => s.id === currentStepId));
    }
  }, [surveySteps, currentStepId]);

  if (!currentStep) return null;

  // Save a single answer to the backend immediately
  const saveAnswer = async (stepId: string, value: any) => {
    try {
      setSaving(true);
      await postSurveyFill(
        {
          entity_type: "user",
          entity_id: userId,
          answers: { [stepId]: value },
        },
        "default"
      );
      setAnsweredIds((prev) => new Set(prev).add(stepId));
    } catch (err) {
      console.error("Error saving answer:", err);
    } finally {
      setSaving(false);
    }
  };

  // Get the summary step
  const getSummaryStep = () =>
    surveySteps.find((s) => s.type === "summary");

  // Determine the natural next step from the chain
  const getChainNextId = (step: Step, value: any): string | undefined => {
    if (step.options) {
      const selectedOpt = step.options.find((opt) => opt.value === value);
      if (selectedOpt?.next) return selectedOpt.next;
    }
    if (typeof value === "object" && value?.next) return value.next;
    if (typeof step.next_step === "string" && step.next_step) return step.next_step;
    return undefined;
  };

  // Find the next unanswered step walking the chain from nextId
  const findNextUnanswered = (
    startId: string | undefined,
    answered: Set<string>
  ): string | undefined => {
    const summaryStep = getSummaryStep();
    const summaryId = summaryStep?.id || "summary";
    const stepsById = new Map(surveySteps.map((s) => [s.id, s]));
    let id = startId;

    // Walk the static chain skipping answered steps
    while (id && id !== summaryId) {
      if (!answered.has(id)) return id; // found an unanswered step
      const step = stepsById.get(id);
      if (!step) break;
      // Follow static next_step only (we don't know the user's answer for skipped steps)
      id = typeof step.next_step === "string" ? step.next_step : undefined;
    }

    return id; // either summaryId or undefined
  };

  const handleAnswer = async (value: any) => {
    await new Promise((resolve) => setTimeout(resolve, 500));

    // 1. Save this answer to the backend immediately
    await saveAnswer(currentStep.id, value);

    const summaryStep = getSummaryStep();
    const summaryId = summaryStep?.id || "summary";
    const newAnswered = new Set(answeredIds).add(currentStep.id);

    // 2. Determine next step
    let nextId: string | undefined;

    if (isResuming) {
      // Resume mode: find the next missing step in the missingStepIds list
      const currentIdx = missingStepIds.indexOf(currentStep.id);
      const nextMissingId = currentIdx !== -1
        ? missingStepIds[currentIdx + 1]
        : missingStepIds.find((id) => !newAnswered.has(id));

      nextId = nextMissingId || summaryId;
    } else {
      // Full mode: follow the chain, skip answered steps
      const chainNext = getChainNextId(currentStep, value);
      nextId = findNextUnanswered(chainNext, newAnswered);
      if (!nextId) nextId = summaryId;
    }

    // 3. Navigate
    if (nextId === summaryId || nextId === "summary") {
      // Send summary=true to mark survey as complete
      await postSurveyFill(
        { entity_type: "user", entity_id: userId, answers: { summary: true } },
        "default"
      ).catch(() => {});

      if (summaryStep) {
        setHistory((prev) => [...prev, currentStep.id]);
        setCurrentStepId(summaryId);
      }
      return;
    }

    if (nextId) {
      setHistory((prev) => [...prev, currentStep.id]);
      setCurrentStepId(nextId);
    }
  };

  const handleBack = () => {
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const newHistory = [...prev];
      const prevStepId = newHistory.pop();
      if (prevStepId) setCurrentStepId(prevStepId);
      return newHistory;
    });
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-[#F5F6FA] font-['Plus_Jakarta_Sans',sans-serif] overflow-y-auto">
      {/* Background Gradient - Fixed to cover entire viewport */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(62, 62, 255, 0) 0%, rgba(62, 110, 255, 0.2) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
        }}
      />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-6 sm:py-8 md:py-10 lg:py-12">
        <div className="w-full max-w-2xl mx-auto p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl text-center">
          {currentStep.type !== "summary" && (
            <h1 className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[58px] leading-[1.1] mb-4 sm:mb-6 md:mb-8 text-center sm:text-start">
              {dictionary[currentStep.question_text][language as Language] ||
                currentStep.question_text}
            </h1>
          )}
          {currentStep.description && (
            <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6 text-center sm:text-start">
              {dictionary[currentStep.description][language as Language] ||
                currentStep.description}
            </p>
          )}

          {currentStep.type === "single" && currentStep.options && (
            <div className="flex flex-col gap-3">
              {currentStep.options.map((opt, index) => (
                <ButtonAnswer
                  key={index.toString()}
                  onClick={() => handleAnswer(opt.value)}
                  option={opt}
                  language={language}
                  country={country || ""}
                  currentStepId={currentStepId}
                />
              ))}
            </div>
          )}

          {currentStep.type === "multi" && currentStep.options && (
            <MultiSelectStep
              options={currentStep.options}
              onSubmit={(values) => handleAnswer(values)}
              language={language}
            />
          )}

          {currentStep.type === "text" && (
            <TextStep
              language={language}
              key={currentStep.id.toString()}
              onSubmit={(value) => handleAnswer(value)}
            />
          )}

          {currentStep.type === "textarea" && (
            <TextAreaStep
              language={language}
              key={currentStep.id.toString()}
              onSubmit={(value) => handleAnswer(value)}
            />
          )}

          {currentStep.type === "phone" && (
            <PhoneStep
              language={language}
              key={currentStep.id.toString()}
              onSubmit={(value) => handleAnswer(value)}
            />
          )}

          {currentStep.type === "summary" && (
            <SummaryStep language={language} />
          )}

          {currentStep.type !== "summary" && history.length > 0 && (
            <div
              onClick={handleBack}
              className="mt-4 sm:mt-6 flex items-center justify-center sm:justify-start gap-2 cursor-pointer hover:text-black transition-all"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
              <p className="text-sm sm:text-base md:text-lg font-medium text-gray-500">
                {dictionary["back"][language as Language] || "Volver"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ButtonAnswer = ({
  key,
  onClick,
  option,
  language,
  country,
  currentStepId,
}: {
  key: string;
  onClick: () => void;
  option: Option;
  language: Language;
  country: string;
  currentStepId: string;
}) => {
  const [selected, setSelected] = useState<any>(null);
  const handleClick = () => {
    setSelected({ value: option.value, key: option.next });
    onClick();
  };

  return (
    <button
      key={key}
      onClick={handleClick}
      className={`
                w-full
                text-base 
                sm:text-lg 
                md:text-xl 
                lg:text-2xl 
                py-3 
                sm:py-4 
                md:py-6 
                lg:py-8 
                px-4 
                sm:px-5 
                md:px-6 
                font-medium 
                hover:shadow-md 
                active:scale-95
                text-black 
                rounded-xl 
                border 
                border-gray-200 
                transition-all 
                duration-200
                shadow-sm 
                ${
                  selected?.value === option.value &&
                  selected?.key === option.next
                    ? "bg-[#0E172A] text-white"
                    : "bg-white"
                }
            `}
    >
      {country !== "CO" && currentStepId === "monthlySales"
        ? dictionary[option.label + "-USD"][language as Language] ||
          option.label
        : dictionary[option.label][language as Language] || option.label}
    </button>
  );
};

interface MultiSelectStepProps {
  options: Step["options"];
  onSubmit: (values: string[]) => void;
  language: Language;
}

function MultiSelectStep({
  options = [],
  onSubmit,
  language,
}: MultiSelectStepProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const toggleOption = (value: string) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  return (
    <div className="flex flex-col gap-2 sm:gap-3">
      {options.map((opt) => (
        <button
          key={opt.value.toString()}
          onClick={() => toggleOption(opt.value.toString())}
          className={`w-full border py-3 sm:py-4 md:py-6 lg:py-8 px-4 sm:px-5 md:px-6 rounded-xl transition-all duration-200 active:scale-95 text-start text-base sm:text-lg md:text-xl lg:text-2xl font-bold hover:shadow-md ${
            selected.includes(opt.value.toString())
              ? "bg-[#0E172A] text-white"
              : "bg-white text-gray-700 border-gray-300"
          }`}
        >
          {dictionary[opt.label][language as Language] || opt.label}
        </button>
      ))}
      <ButtonContinue
        onClick={() => onSubmit(selected)}
        disabled={selected.length === 0}
        text={dictionary["continue"][language as Language] || "Continuar"}
      />
    </div>
  );
}

interface TextAreaStepProps {
  onSubmit: (value: string) => void;
  language: Language;
}

function TextAreaStep({ onSubmit, language }: TextAreaStepProps) {
  const [value, setValue] = useState("");
  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <textarea
        className="border rounded-lg p-3 sm:p-4 text-sm sm:text-base w-full h-[150px] sm:h-[180px] md:h-[200px] resize-none focus:outline-none focus:ring-2 focus:ring-[#0E172A]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={
          dictionary["write-your-answer-here"][language as Language] ||
          "Escribe tu respuesta aquí..."
        }
      />
      <ButtonContinue
        onClick={() => onSubmit(value)}
        disabled={!value.trim()}
        text={dictionary["continue"][language as Language] || "Continuar"}
      />
    </div>
  );
}

interface TextStepProps {
  onSubmit: (value: string) => void;
  language: Language;
}

function TextStep({ onSubmit, language }: TextStepProps) {
  const [value, setValue] = useState("");
  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <input
        type="text"
        className="border rounded-xl p-3 sm:p-4 text-base sm:text-lg w-full focus:outline-none focus:ring-2 focus:ring-[#0E172A] bg-white border-gray-200"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={
          dictionary["write-your-answer-here"][language as Language] ||
          "Escribe tu respuesta aquí..."
        }
      />
      <ButtonContinue
        onClick={() => onSubmit(value)}
        disabled={!value.trim()}
        text={dictionary["continue"][language as Language] || "Continuar"}
      />
    </div>
  );
}

function SummaryStep({
  language,
}: {
  language: Language;
}) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex items-center justify-center bg-[#0E172A] rounded-full p-3 sm:p-4 mb-4 sm:mb-6">
        <Check className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-white" />
      </div>
      <h1 className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[58px] leading-[1.1] mb-6 sm:mb-8 text-[#0E172A] text-center">
        {dictionary["all-set"][language as Language] || "Todo listo"}
      </h1>
      <ButtonContinue
        onClick={() => {
          if (window.innerWidth < 768) {
            navigate("/settings");
          } else {
            navigate("/home");
          }
        }}
        disabled={false}
        text={
          dictionary["go-to-account"][language as Language] || "Ir a mi cuenta"
        }
      />
    </div>
  );
}

interface PhoneStepProps {
  onSubmit: (value: string) => void;
  language: Language;
}

function PhoneStep({ onSubmit, language }: PhoneStepProps) {
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_SURVEY_COUNTRY);
  const [phoneInput, setPhoneInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredCountries = WORLD_COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dialCode.includes(search)
  );

  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [dropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const phoneNumber = phoneInput.trim()
    ? `${selectedCountry.dialCode} ${phoneInput.trim()}`
    : "";

  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="flex gap-2">
        {/* Country code dropdown */}
        <div ref={dropdownRef} className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setDropdownOpen((v) => !v)}
            className="h-full bg-white px-3 py-3 sm:py-4 border border-gray-200 rounded-xl flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-[#0E172A] min-w-[110px]"
          >
            <span
              className={`fi fi-${selectedCountry.code.toLowerCase()} rounded-sm`}
              style={{ width: "1.4em", height: "1.05em", display: "inline-block" }}
            />
            <span className="text-sm font-medium text-gray-700">{selectedCountry.dialCode}</span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
              {/* Search */}
              <div className="p-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={
                      dictionary["search-country-placeholder"]?.[language] ||
                      "Buscar país o código..."
                    }
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E172A]"
                  />
                </div>
              </div>

              {/* Country list */}
              <ul className="max-h-52 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((c) => (
                    <li key={c.code}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCountry(c);
                          setDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
                          c.code === selectedCountry.code ? "bg-gray-100 font-semibold" : ""
                        }`}
                      >
                        <span
                          className={`fi fi-${c.code.toLowerCase()} rounded-sm flex-shrink-0`}
                          style={{ width: "1.4em", height: "1.05em", display: "inline-block" }}
                        />
                        <span className="flex-1 truncate text-gray-800">{c.name}</span>
                        <span className="text-gray-400 flex-shrink-0 text-xs">{c.dialCode}</span>
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="px-4 py-4 text-sm text-gray-400 text-center">
                    {dictionary["no-countries-found"]?.[language] || "No se encontraron países"}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Phone number input */}
        <div className="relative flex-1">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="tel"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            className="w-full bg-white pl-12 pr-4 py-3 sm:py-4 text-base border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0E172A]"
            placeholder={
              dictionary["phone-number-placeholder"]?.[language] || "300 123 4567"
            }
          />
        </div>
      </div>

      <ButtonContinue
        onClick={() => onSubmit(phoneNumber)}
        disabled={!phoneInput.trim()}
        text={dictionary["continue"][language as Language] || "Continuar"}
      />
    </div>
  );
}

const ButtonContinue = ({
  onClick,
  disabled,
  text = "Continuar",
}: {
  onClick: () => void;
  disabled: boolean;
  text?: string;
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center gap-2 mt-4 sm:mt-6 bg-[#0E172A] hover:bg-[#0E172A]/80 active:bg-[#0E172A]/70 text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white py-3 sm:py-4 px-5 sm:px-6 w-full sm:w-auto rounded-xl disabled:opacity-40 transition-all duration-300 hover:scale-105 active:scale-95"
    >
      <span>{text}</span>
      <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white flex-shrink-0" />
    </button>
  );
};
